import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, Loader2, RefreshCw, Save, Shuffle, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { fetchApprovedMaps, mapImageUrl, KZ_MODES } from '../../lib/cs2kz';
import { WEEKDAYS, addDays, formatInZone } from '../../lib/kzTime';
import { fetchWeekBoard, fetchTotals } from '../../lib/motwBoard';
import {
  DEFAULT_CONFIG,
  getConfig,
  saveConfig,
  eventDateFor,
  eligibleCourses,
  getOrCreateWeek,
  getWeek,
  setWeekCourse,
  resetWeekSessions,
  listMotwPlayers,
} from '../../database/mapOfTheWeek';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { NativeSelect } from '../ui/select-native';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import PageHeader from './shell/PageHeader';

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toFixed(3).padStart(6, '0')}`;
};

// YYYY-MM of an event date, for the month picker.
const monthOf = (dateKey) => dateKey.slice(0, 7);

// Every event date in a month, walking back from the month's own weeks.
const eventDatesInMonth = (config, month) => {
  const dates = [];
  // Start from the first event date on or after the 1st, then step by weeks.
  let cursor = eventDateFor(config, Date.parse(`${month}-01T12:00:00Z`));
  if (cursor < `${month}-01`) cursor = addDays(cursor, 7);
  while (monthOf(cursor) === month) {
    dates.push(cursor);
    cursor = addDays(cursor, 7);
  }
  return dates;
};

const MapOfTheWeekManagement = () => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(DEFAULT_CONFIG);
  const [week, setWeek] = useState(null);
  const [players, setPlayers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');

  const [boards, setBoards] = useState(null);
  const [boardsLoading, setBoardsLoading] = useState(false);

  const [month, setMonth] = useState('');
  const [totals, setTotals] = useState(null);
  const [totalsLoading, setTotalsLoading] = useState(false);

  const eventDate = useMemo(() => (config ? eventDateFor(config) : null), [config]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await getConfig();
      const date = eventDateFor(settings);
      const [weekDoc, playerList, maps] = await Promise.all([
        getOrCreateWeek(settings, date),
        listMotwPlayers(),
        fetchApprovedMaps(),
      ]);
      setConfig(settings);
      setForm(settings);
      setWeek(weekDoc);
      setPlayers(playerList);
      setCourses(eligibleCourses(maps));
      setMonth(monthOf(date));
      setBoards(null);
      setTotals(null);
    } catch (err) {
      toast.error('Could not load Map of the Week.', { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateSession = (index, patch) =>
    setForm((prev) => ({
      ...prev,
      sessions: prev.sessions.map((session, i) =>
        i === index ? { ...session, ...patch } : session
      ),
    }));

  const handleSave = async (e) => {
    e.preventDefault();
    const badTime = form.sessions.find((s) => !/^\d{2}:\d{2}$/.test(s.startTime));
    if (badTime) {
      toast.error('Start times must look like 21:00.');
      return;
    }
    const badLength = form.sessions.find(
      (s) => !Number.isFinite(Number(s.durationMinutes)) || Number(s.durationMinutes) < 1
    );
    if (badLength) {
      toast.error('Window length must be at least 1 minute.');
      return;
    }
    try {
      // Intl throws on a bogus zone — cheaper to find out here than on the page.
      Intl.DateTimeFormat(undefined, { timeZone: form.timeZone });
    } catch {
      toast.error('That is not a valid time zone.', { description: 'e.g. Europe/Berlin, UTC' });
      return;
    }

    setSaving(true);
    try {
      await saveConfig(form);
      toast.success('Schedule saved.', {
        description: 'Weeks that already exist keep their windows until you re-apply them.',
      });
      await load();
    } catch (err) {
      toast.error('Could not save the schedule.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSwapCourse = async () => {
    if (!selectedCourse) return;
    const entry = courses.find((c) => `${c.map.id}:${c.courseIndex}` === selectedCourse);
    if (!entry) return;
    setSwapping(true);
    try {
      const updated = await setWeekCourse(config, eventDate, entry);
      setWeek(updated);
      setBoards(null);
      toast.success(`This week is now ${entry.map.name} — ${entry.course.name}.`);
    } catch (err) {
      toast.error('Could not change the course.', { description: err.message });
    } finally {
      setSwapping(false);
    }
  };

  const handleReapplyWindows = async () => {
    setSwapping(true);
    try {
      const updated = await resetWeekSessions(config, eventDate);
      setWeek(updated);
      toast.success('Windows re-applied from the schedule.');
    } catch (err) {
      toast.error('Could not update the windows.', { description: err.message });
    } finally {
      setSwapping(false);
    }
  };

  const loadBoards = async () => {
    setBoardsLoading(true);
    try {
      const rankedModes = KZ_MODES.filter((mode) => week.modes?.[mode.id]?.ranked);
      const results = await Promise.all(
        rankedModes.map((mode) =>
          fetchWeekBoard({ week, players, mode: mode.id, points: config.points })
        )
      );
      setBoards(rankedModes.map((mode, i) => ({ mode, rows: results[i] })));
    } catch (err) {
      toast.error('Could not load the boards.', { description: err.message });
    } finally {
      setBoardsLoading(false);
    }
  };

  const loadTotals = async () => {
    setTotalsLoading(true);
    try {
      const dates = eventDatesInMonth(config, month);
      const weekDocs = (await Promise.all(dates.map(getWeek))).filter(Boolean);
      if (weekDocs.length === 0) {
        setTotals([]);
        return;
      }
      setTotals(
        await fetchTotals({
          weeks: weekDocs,
          players,
          modes: KZ_MODES,
          points: config.points,
        })
      );
    } catch (err) {
      toast.error('Could not load the month.', { description: err.message });
    } finally {
      setTotalsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeader eyebrow="Map of the Week" title="Map of the Week" />
        <div className="h-40 animate-pulse rounded-md border bg-muted/40" />
      </div>
    );
  }

  const imageUrl = week ? mapImageUrl(week.mapName, week.courseIndex, 'thumbnail') : null;

  return (
    <div>
      <PageHeader
        eyebrow="Weekly event"
        title="Map of the Week"
        description="One course a week, two submission windows. Set the schedule, override the pick, and read the standings."
        breadcrumbs={[{ label: 'Admin', to: '/admin/dashboard' }, { label: 'Map of the Week' }]}
        actions={
          <Button variant="outline" onClick={load}>
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
        }
      />

      <div className="space-y-6 pb-16">
        {/* SCHEDULE ------------------------------------------------------- */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4 text-primary" />
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="flex items-center justify-between rounded-md border px-4 py-3">
                <div>
                  <div className="text-sm font-medium">Event enabled</div>
                  <div className="text-xs text-muted-foreground">
                    Off hides the public page behind a paused notice.
                  </div>
                </div>
                <Switch
                  checked={Boolean(form.enabled)}
                  onCheckedChange={(enabled) => setForm((p) => ({ ...p, enabled }))}
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-3">
                <Field label="Event day">
                  <NativeSelect
                    value={String(form.dayOfWeek)}
                    onChange={(e) => setForm((p) => ({ ...p, dayOfWeek: Number(e.target.value) }))}
                  >
                    {WEEKDAYS.map((day) => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label="Time zone" hint="IANA name, e.g. Europe/Berlin">
                  <Input
                    value={form.timeZone}
                    onChange={(e) => setForm((p) => ({ ...p, timeZone: e.target.value }))}
                  />
                </Field>
                <Field label="Points" hint="1st, 2nd, 3rd — comma separated">
                  <Input
                    value={form.points.join(', ')}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        points: e.target.value
                          .split(',')
                          .map((v) => Number(v.trim()))
                          .filter((v) => Number.isFinite(v)),
                      }))
                    }
                  />
                </Field>
              </div>

              <div className="space-y-3">
                {form.sessions.map((session, i) => (
                  <div
                    key={session.key}
                    className="grid gap-4 rounded-md border p-4 sm:grid-cols-3"
                  >
                    <Field label="Window name">
                      <Input
                        value={session.label}
                        onChange={(e) => updateSession(i, { label: e.target.value })}
                      />
                    </Field>
                    <Field label="Starts at" hint="24h, in the zone above">
                      <Input
                        value={session.startTime}
                        placeholder="21:00"
                        onChange={(e) => updateSession(i, { startTime: e.target.value })}
                      />
                    </Field>
                    <Field label="Open for (minutes)">
                      <Input
                        type="number"
                        min="1"
                        value={session.durationMinutes}
                        onChange={(e) => updateSession(i, { durationMinutes: e.target.value })}
                      />
                    </Field>
                  </div>
                ))}
              </div>

              <Button type="submit" disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save schedule
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* THIS WEEK ------------------------------------------------------ */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shuffle className="h-4 w-4 text-primary" />
              This week · {eventDate}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {!week ? (
              <p className="text-sm text-muted-foreground">No course could be picked this week.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-4">
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt=""
                      className="h-16 w-28 rounded-md border object-cover"
                    />
                  )}
                  <div>
                    <div className="font-mono text-sm font-medium">{week.mapName}</div>
                    <div className="text-xs text-muted-foreground">{week.courseName}</div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {KZ_MODES.filter((m) => week.modes?.[m.id]?.ranked).map((m) => (
                        <Badge key={m.id} variant="secondary">
                          {m.short}
                        </Badge>
                      ))}
                      {week.overridden && <Badge variant="outline">Manual pick</Badge>}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 text-xs text-muted-foreground">
                  {(week.sessions || []).map((session) => (
                    <div key={session.key}>
                      <span className="font-medium text-foreground">{session.label}</span>{' '}
                      {formatInZone(session.startsAt, config.timeZone)} –{' '}
                      {formatInZone(session.endsAt, config.timeZone, { weekday: undefined })} (
                      {config.timeZone})
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[16rem] flex-1">
                    <Field label="Swap this week's course">
                      <NativeSelect
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.target.value)}
                      >
                        <option value="">Choose a course…</option>
                        {courses.map((entry) => (
                          <option
                            key={`${entry.map.id}:${entry.courseIndex}`}
                            value={`${entry.map.id}:${entry.courseIndex}`}
                          >
                            {entry.map.name} — {entry.course.name}
                          </option>
                        ))}
                      </NativeSelect>
                    </Field>
                  </div>
                  <Button onClick={handleSwapCourse} disabled={!selectedCourse || swapping}>
                    {swapping ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Shuffle className="h-4 w-4" />
                    )}
                    Use this course
                  </Button>
                  <Button variant="outline" onClick={handleReapplyWindows} disabled={swapping}>
                    Re-apply windows
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  A week keeps the windows it was created with, so changing the schedule never moves
                  a window players already ran in. Re-apply pushes the current schedule onto this
                  week.
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* WEEK BOARDS ---------------------------------------------------- */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Trophy className="h-4 w-4 text-primary" />
                This week&rsquo;s standings
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={loadBoards}
                disabled={!week || boardsLoading}
              >
                {boardsLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Load standings
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {boards === null && (
              <p className="text-sm text-muted-foreground">
                Reads live times from the CS2KZ API for all {players.length} linked players.
              </p>
            )}
            {boards?.map(({ mode, rows }) => (
              <div key={mode.id} className="space-y-2">
                <div className="text-sm font-medium">
                  {mode.label} <span className="text-muted-foreground">({mode.short})</span>
                </div>
                {rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No times in a window yet.</p>
                ) : (
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-14">#</TableHead>
                          <TableHead>Player</TableHead>
                          <TableHead>Session</TableHead>
                          <TableHead className="text-right">Time</TableHead>
                          <TableHead className="w-20 text-right">Points</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.uid}>
                            <TableCell className="tabular-nums">{row.rank}</TableCell>
                            <TableCell className="font-medium">{row.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{row.sessionLabel}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums">
                              {formatTime(row.timeSeconds)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{row.points}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* MONTH ---------------------------------------------------------- */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Monthly standings</CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-40"
                />
                <Button variant="outline" size="sm" onClick={loadTotals} disabled={totalsLoading}>
                  {totalsLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Load month
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {totals === null && (
              <p className="text-sm text-muted-foreground">
                Adds up every week in the month, across both modes. One API pass per week, so give
                it a moment.
              </p>
            )}
            {totals?.length === 0 && (
              <p className="text-sm text-muted-foreground">No events ran in this month.</p>
            )}
            {totals && totals.length > 0 && (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-14">#</TableHead>
                      <TableHead>Player</TableHead>
                      <TableHead className="text-right">Boards entered</TableHead>
                      <TableHead className="text-right">Wins</TableHead>
                      <TableHead className="w-24 text-right">Points</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {totals.map((row, i) => (
                      <TableRow key={row.uid}>
                        <TableCell className="tabular-nums">{i + 1}</TableCell>
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell className="text-right tabular-nums">{row.entries}</TableCell>
                        <TableCell className="text-right tabular-nums">{row.wins}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {row.points}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MapOfTheWeekManagement;
